import React, { useEffect, useState } from 'react';
import ReactTags from 'react-tag-autocomplete';
import _ from 'underscore';
import api from 'helpers/api';
import notification from 'helpers/notification';
import { useExtendState } from 'helpers/use_extend_state';
import { useOnRouteChange } from 'helpers/use_on_route_change';
import { colorPickerColors } from 'constants/colors';
import Button from '../../components/button';
import Label from '../../components/label';
import Input from '../../components/input';
import Textarea from '../../components/textarea';
import ErrorMessage from '../../components/error_message';
import ColorPicker from '../../components/color_picker';
import Checkbox from '../../components/checkbox';
import useGym from 'helpers/use_gym';
import { spell } from 'helpers/international_spelling';

interface Flag {
  title?: string;
  description?: string;
  tag_list: string[];
  color?: string;
  public_flag: boolean;
}

const AddContactFlagModal = ({ modalInstance, contact }) => {
  const gym = useGym();
  const [isLoading, setIsLoading] = useState(false);
  const [flag, extendFlag, setFlag] = useExtendState<Flag>({
    tag_list: [],
    public_flag: gym.gym_config.contact_flag_public_default,
  });
  const [suggestions, setSuggestions] = useState([]);
  const [errors, setErrors] = useState<any>({});

  const url = `/gyms/${gym.id}/contacts/${contact.id}/flags`;

  const close = () => {
    modalInstance.dismiss('cancel');
  };

  useOnRouteChange(() => {
    close();
  });

  useEffect(() => {
    const url = `/gyms/${gym.id}/tags/flags_all`;
    api.get(url).then((response) => {
      setSuggestions(response.map((name, index) => ({ id: index, name })));
    });
  }, []);

  const onDelete = (tagIndex) => {
    setFlag((data) => ({
      ...data,
      tag_list: data.tag_list.filter((tag, index) => tagIndex !== index),
    }));
  };

  const onAddition = (newTag) => {
    setFlag((data) => ({
      ...data,
      tag_list: _.uniq(data.tag_list.concat(newTag.name)),
    }));
  };

  const save = () => {
    setIsLoading(true);
    setErrors({});
    api.post(url, { contact_flag: flag }).then(
      () => {
        notification.success('Flag added successfully');
        modalInstance.close();
      },
      (response) => {
        setIsLoading(false);
        setErrors(response.data.errors);

        notification.error('Request could not be completed. Please try again');
      },
    );
  };

  return (
    <>
      <div className="drawer__backdrop" onClick={() => close()}></div>
      <div className="drawer">
        <div className="drawer__title">Add Flag</div>
        <button type="button" className="drawer__close" onClick={() => close()}>
          <i className="icon-cross" />
        </button>

        <div className="drawer__inner">
          <div className="margin-bottom-16">
            <Label>Contact</Label>
            <Input value={contact.name} disabled />
          </div>

          <div className="margin-bottom-16">
            <Label>Title</Label>
            <Input
              value={flag.title}
              onChange={(title) => extendFlag({ title })}
              placeholder="Optional"
            />
          </div>

          <div className="margin-bottom-16">
            <Label>Description</Label>
            <Textarea
              value={flag.description}
              onChange={(description) => extendFlag({ description })}
            />
            <ErrorMessage error={errors.description} />
          </div>

          <div className="margin-bottom-16">
            <Label>Tag</Label>
            <ReactTags
              suggestions={suggestions}
              onDelete={onDelete}
              onAddition={onAddition}
              tags={flag.tag_list.map((name) => ({ name }))}
              placeholderText="Add tag"
              allowNew
              addOnBlur
            />
          </div>

          <div className="margin-bottom-16">
            <label className="control-label">{spell('Color', gym.country)}</label>
            <div>
              <ColorPicker
                colors={colorPickerColors}
                value={flag.color}
                onChange={(color) => extendFlag({ color })}
                variant="v2"
              />
            </div>
            <ErrorMessage error={errors.color} />
          </div>

          <div className="margin-bottom-16">
            <Checkbox
              variant="v2"
              checked={flag.public_flag}
              onChange={(public_flag) => extendFlag({ public_flag })}
            >
              Show in Kiosk Mode?
            </Checkbox>
          </div>
        </div>
        <div className="drawer__footer">
          <Button onClick={() => close()} size="sm" variant="hollow">
            Cancel
          </Button>
          <Button onClick={() => save()} disabled={isLoading} size="sm" variant="success">
            Save
          </Button>
        </div>
      </div>
    </>
  );
};

angular.module('shared').value('AddContactFlagModal', AddContactFlagModal);
